import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Upload, Camera, Send, MoonStar, User as UserIcon, LogOut, History, MicOff, Settings } from "lucide-react";
import { toast } from "sonner";
import ChatBubble from "../components/ChatBubble";
import ThinkingAnimation from "../components/ThinkingAnimation";
import ItineraryView from "../components/ItineraryView";
import TripHistory from "../components/TripHistory";
import PersonaBubbles from "../components/PersonaBubbles";
import LanguageSwitcher from "../components/LanguageSwitcher";
import MalaysiaBackdrop from "../components/MalaysiaBackdrop";
import { sendChat, transcribeVoice, extractUpload, saveTrip } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT, greetingKey } from "../lib/i18n";
import { track } from "../lib/analytics";

function newSessionId() {
  return "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

const PLANNING_TIMEOUT_MS = 90000;

// Persona opening phrases — kept in ms/en. The backend attaches a persona brief;
// we just seed the first user message so the flow feels natural.
const PERSONA_OPENERS = {
  ms: {
    family: "Saya nak rancang perjalanan keluarga.",
    honeymoon: "Saya nak rancang perjalanan Bulan Madu.",
    riders: "Saya nak rancang perjalanan Kembara.",
    food: "Saya nak rancang perjalanan berfokuskan Makanan.",
    shopping: "Saya nak rancang perjalanan Membeli-belah.",
    beach: "Saya nak percutian pantai.",
    nature: "Saya nak rancang perjalanan Alam Semula Jadi.",
    themepark: "Saya nak rancang perjalanan Taman Tema.",
  },
  en: {
    family: "I'd like to plan a family trip.",
    honeymoon: "I'd like to plan a honeymoon.",
    riders: "I'd like to plan a Touring trip.",
    food: "I want a food-focused trip.",
    shopping: "I want a shopping-focused trip.",
    beach: "I want a beach getaway.",
    nature: "I want a nature & outdoor trip.",
    themepark: "I want a theme-park trip.",
  },
};

export default function ChatScreen() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("taip_session") || newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [planningError, setPlanningError] = useState(null);
  const [trip, setTrip] = useState(null);
  const [persona, setPersona] = useState(null); // key: "family" | ... | null
  const [showHistory, setShowHistory] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const startedRef = useRef(false); // fire conversation_started once per session
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const { user, login, logout } = useAuth();
  const { t, lang } = useT();

  useEffect(() => {
    localStorage.setItem("taip_session", sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, trip]);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      abortControllerRef.current?.abort();
    },
    []
  );

  const send = async (
    text,
    contextHint,
    personaOverride,
    { appendUser = true } = {}
  ) => {
    if (!text || !text.trim()) return;
    const requestId = ++requestIdRef.current;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, PLANNING_TIMEOUT_MS);
    const activePersona = personaOverride ?? persona;
    const langNote = lang === "en" ? "\n\n[Language: reply in English.]" : "";
    const finalText = text + langNote;

    if (appendUser) {
      setMessages((m) => [...m, { role: "user", content: text }]);
    }
    setInput("");
    setPlanningError(null);
    setThinking(true);

    if (!startedRef.current) {
      startedRef.current = true;
      track("conversation_started", { persona: activePersona, lang });
    }

    try {
      const res = await sendChat(
        sessionId,
        finalText,
        contextHint,
        activePersona,
        { signal: controller.signal }
      );
      if (requestId !== requestIdRef.current) return;

      setMessages((m) => [...m, { role: "assistant", content: res.reply, chips: res.chips, trip: res.trip }]);
      if (res.trip) {
        setTrip(res.trip);
        track("travel_book_generated", {
          destination: res.trip?.destination,
          days: res.trip?.days,
          persona: activePersona,
          lang,
        });
        track("conversation_completed", { persona: activePersona, lang });
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      const status = e.response?.status;
      if (timedOut) {
        toast.error(t("planning.timeout_title"));
        setPlanningError({
          text,
          contextHint,
          persona: activePersona,
          kind: "timeout",
        });
      } else if (status === 401) {
        toast.error(t("err.session_expired"));
        setTimeout(() => (window.location.href = "/login"), 800);
      } else if (status === 402) {
        toast.info(lang === "en" ? "Full Access required to continue." : "Full Access diperlukan untuk teruskan.");
        setTimeout(() => (window.location.href = "/paywall"), 800);
      } else if (status === 429) {
        toast.warning(t("err.rate_limited"));
        setPlanningError({ text, contextHint, persona: activePersona });
      } else {
        toast.error(t("err.chat_failed"), { description: t("err.connection") });
        setPlanningError({ text, contextHint, persona: activePersona });
      }
    } finally {
      window.clearTimeout(timeoutId);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      if (requestId === requestIdRef.current) {
        setThinking(false);
      }
    }
  };

  const handlePersonaSelect = (p) => {
    setPersona(p.key);
    track("persona_selected", { persona: p.key, lang });
    const opener = (PERSONA_OPENERS[lang] || PERSONA_OPENERS.ms)[p.key];
    send(opener, undefined, p.key);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    toast.loading(t("toast.reading_doc"), { id: "upload" });
    try {
      const res = await extractUpload(file);
      toast.success(t("toast.doc_ok"), { id: "upload" });
      track("upload_used", { kind: file.type || "unknown" });
      setMessages((m) => [...m, { role: "user", content: `📎 ${file.name}` }]);
      await send(lang === "en" ? "I uploaded this document. Please use it." : "Saya muat naik dokumen ini. Sila guna maklumat ini.", res.summary);
    } catch {
      toast.error(t("toast.doc_fail"), { id: "upload" });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        toast.loading(t("toast.voice_processing"), { id: "voice" });
        try {
          const res = await transcribeVoice(blob);
          toast.success(t("toast.voice_ok"), { id: "voice" });
          track("voice_used");
          if (res.text) send(res.text);
        } catch {
          toast.error(t("toast.voice_fail"), { id: "voice" });
        }
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error(t("toast.mic_deny"));
    }
  };
  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const startNewTrip = () => {
    requestIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    const id = newSessionId();
    setSessionId(id);
    setMessages([]);
    setTrip(null);
    setPersona(null);
    setInput("");
    setThinking(false);
    setPlanningError(null);
    startedRef.current = false;
  };

  const handleSaveTrip = async () => {
    if (!trip) return;
    if (!user) {
      toast.info(t("toast.login_first"));
      login();
      return;
    }
    try {
      await saveTrip(sessionId, trip.destination || "Travel Book", trip);
      toast.success(t("toast.trip_saved"));
      track("travel_book_saved", { destination: trip.destination, days: trip.days });
    } catch {
      toast.error(t("toast.save_fail"));
    }
  };

  const hasStarted = messages.length > 0 || thinking;
  const useDarkChrome = !hasStarted;
  const welcomeKey = user ? "greet.welcome_back" : "greet.welcome_guest";
  const greet = t(greetingKey());
  const welcome = t(welcomeKey);

  return (
    <div
      className={`min-h-screen flex flex-col relative transition-colors duration-500 ${
        hasStarted ? "bg-[#FBFBFD]" : "bg-[#0B1F4D]"
      }`}
      aria-busy={thinking}
    >
      {/* Subtle Malaysian travel-themed backdrop — decorative only, sits behind everything */}
      {!hasStarted && <MalaysiaBackdrop />}
      {/* Top bar */}
      <header
        data-testid="topbar"
        className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl border-b transition-colors duration-500 ${
          useDarkChrome
            ? "bg-[#0B1F4D]/72 border-white/10 shadow-[0_6px_24px_rgba(5,15,40,0.14)]"
            : "bg-white/70 border-[#E8E8ED]"
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <button
            data-testid="new-trip-btn"
            onClick={startNewTrip}
            className="flex items-center gap-2 group"
          >
            <img
              src="/taip-logo.png"
              alt="TAi-P"
              className={`w-10 h-10 rounded-full object-cover bg-white ${
                useDarkChrome
                  ? "shadow-[0_0_22px_rgba(214,179,106,0.26)] ring-1 ring-[#F3E3B2]/35"
                  : "shadow-[0_4px_16px_rgba(10,37,64,0.15)]"
              }`}
            />
            <div className="hidden sm:block">
              <div className={`font-semibold leading-none ${useDarkChrome ? "text-[#F8FAFF]" : "text-[#0A2540]"}`}>TAi-P</div>
              <div className={`text-[10px] leading-none mt-0.5 ${useDarkChrome ? "text-[#AEBBD3]" : "text-gray-500"}`}>Travel Intelligence Companion</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            {/* Muslim Friendly — now a static premium badge (display-only) */}
            <span
              data-testid="muslim-friendly-badge"
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium bg-gradient-to-r from-[#F3E3B2] to-[#D6B36A] text-[#0B1F4D] border border-[#F3E3B2]/60 shadow-[0_4px_18px_rgba(214,179,106,0.18)] select-none cursor-default"
              title={t("topbar.muslim_friendly_badge")}
            >
              <MoonStar className="w-3 h-3" />
              {t("topbar.muslim_friendly")}
            </span>
            {/* Language selector — visible on every screen */}
            <LanguageSwitcher />
            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setShowMenu((v) => !v)}
                  className="w-9 h-9 rounded-full overflow-hidden border border-[#E8E8ED]"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#F5F5F7] flex items-center justify-center">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                    </div>
                  )}
                </button>
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.08)] border border-[#E8E8ED] p-2 z-50">
                    <div className="px-3 py-2 border-b border-[#F5F5F7] mb-1">
                      <div className="text-sm font-medium text-[#1D1D1F] truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    <button
                      data-testid="menu-history-btn"
                      onClick={() => { setShowHistory(true); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F5F7] text-sm text-[#1D1D1F]"
                    >
                      <History className="w-4 h-4" /> {t("menu.history")}
                    </button>
                    <a
                      data-testid="menu-settings-btn"
                      href="/settings"
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F5F7] text-sm text-[#1D1D1F]"
                    >
                      <Settings className="w-4 h-4" /> {t("menu.settings")}
                    </a>
                    <button
                      data-testid="menu-logout-btn"
                      onClick={() => { logout(); setShowMenu(false); toast.success(t("toast.logout_ok")); }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F5F7] text-sm text-[#1D1D1F]"
                    >
                      <LogOut className="w-4 h-4" /> {t("menu.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <a
                data-testid="login-btn"
                href="/login"
                className={`chip px-4 py-1.5 rounded-full text-xs font-medium text-white border transition-colors ${
                  useDarkChrome
                    ? "bg-[#123B8F]/72 border-white/20 hover:bg-[#2E63C7]/80 shadow-[0_4px_18px_rgba(5,15,40,0.2)]"
                    : "bg-[#0A2540] border-[#0A2540] hover:bg-[#0A2540]/90"
                }`}
              >
                {t("topbar.login")}
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Chat area */}
      <main
        ref={scrollRef}
        className="relative z-10 flex-1 overflow-y-auto scroll-clean pt-20 pb-36 max-w-4xl w-full mx-auto px-4 md:px-6"
      >
        {!hasStarted && (
          <div className="min-h-[calc(100vh-14rem)] grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] gap-8 md:gap-12 items-center">
            {/* Left column — copy + persona bubbles */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              {/* Greeting: time-based + login-state-aware welcome line */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08, duration: 0.4 }}
                data-testid="greeting"
                className="text-sm sm:text-base text-[#AEBBD3]"
              >
                {greet}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.4 }}
                data-testid="welcome"
                className="text-sm sm:text-base text-[#F8FAFF] mt-1"
              >
                {welcome}
              </motion.div>

              {/* Main heading + subtitle — tightened spacing above; ~22% smaller than the earlier hero size */}
              <motion.h1
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22, duration: 0.5 }}
                data-testid="landing-heading"
                className="taip-hero-heading whitespace-pre-line max-w-xl text-[2rem] sm:text-[2.6rem] lg:text-[2.9rem] font-medium text-[#F8FAFF] mt-4 [text-shadow:0_4px_24px_rgba(5,15,40,0.3)]"
              >
                <span className="taip-hero-heading-main">
                  {t("landing.heading_main")}
                </span>{" "}
                <span className="taip-hero-script">
                  {t("landing.heading_script")}
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                data-testid="landing-subtitle"
                className="whitespace-pre-line text-sm sm:text-base leading-relaxed text-[#AEBBD3] mt-3 max-w-md"
              >
                {t("landing.subtitle")}
              </motion.p>

              {/* Persona Bubbles — TAi-P trademark */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.42, duration: 0.5 }}
                className="mt-10 w-full"
              >
                <PersonaBubbles onSelect={handlePersonaSelect} active={persona} />
                <div className="text-xs text-[#AEBBD3] mt-3 md:text-left text-center">
                  {t("landing.prompt")}
                </div>
              </motion.div>
            </div>

            {/* Right column — subtle travel illustration (desktop only) */}
            <div
              className="hidden min-h-[340px] md:block"
              data-testid="hero-illustration"
              aria-hidden="true"
            />
          </div>
        )}

        {hasStarted && (
          <div className="space-y-3 sm:space-y-3.5">
            {messages.map((m, i) => (
              <div key={i}>
                <ChatBubble role={m.role} content={m.content} />
                {m.trip && (
                  <div className="mt-6">
                    <ItineraryView trip={m.trip} onSave={handleSaveTrip} sessionId={sessionId} />
                  </div>
                )}
              </div>
            ))}
            {thinking && <ThinkingAnimation persona={persona} immersive={messages.length <= 1} />}
            {planningError && (
              <div
                role="alert"
                className="rounded-2xl border border-[#9E2A2B]/15 bg-white px-5 py-4 shadow-[0_8px_28px_rgba(10,37,64,0.06)]"
              >
                <p className="text-sm font-medium text-[#0A2540]">
                  {t(
                    planningError.kind === "timeout"
                      ? "planning.timeout_title"
                      : "planning.error_title"
                  )}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {t(
                    planningError.kind === "timeout"
                      ? "planning.timeout_body"
                      : "planning.error_body"
                  )}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      send(
                        planningError.text,
                        planningError.contextHint,
                        planningError.persona,
                        { appendUser: false }
                      )
                    }
                    className="rounded-full bg-[#0A2540] px-4 py-2 text-xs font-medium text-white"
                  >
                    {t("err.retry")}
                  </button>
                  <button
                    type="button"
                    onClick={startNewTrip}
                    className="rounded-full border border-[#E8E8ED] px-4 py-2 text-xs font-medium text-[#0A2540]"
                  >
                    {t("planning.back")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Input bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-30 pb-4 pt-8 px-4 md:px-6 ${
          hasStarted
            ? "bg-gradient-to-t from-[#FBFBFD] via-[#FBFBFD]/95 to-transparent"
            : "bg-gradient-to-t from-[#0B1F4D] via-[#0B1F4D]/92 to-transparent"
        }`}
      >
        <div className={`max-w-4xl mx-auto transition-all duration-300 ${thinking ? "opacity-55 scale-[0.99]" : "opacity-100"}`}>
          <div
            data-testid="input-bar"
            className={`bg-white/95 backdrop-blur-2xl rounded-full pl-5 pr-2 py-2 flex items-center gap-2 border ${
              hasStarted
                ? "shadow-[0_12px_48px_rgba(0,0,0,0.08)] border-[#E8E8ED]"
                : "shadow-[0_16px_42px_rgba(5,15,40,0.26),0_0_18px_rgba(214,179,106,0.07)] border-white/20"
            }`}
          >
            <input
              data-testid="chat-input"
              value={input}
              disabled={thinking}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(input); }}
              placeholder={t("input.placeholder")}
              className="flex-1 bg-transparent outline-none text-[15px] text-[#1D1D1F] placeholder:text-gray-500 py-2"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }}
              data-testid="file-upload-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }}
              data-testid="camera-input"
            />
            <button
              data-testid="upload-btn"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-full hover:bg-[#F5F5F7] text-gray-500 transition-colors"
              title="Upload"
            >
              <Upload className="w-5 h-5" />
            </button>
            <button
              data-testid="camera-btn"
              onClick={() => cameraInputRef.current?.click()}
              className="p-2 rounded-full hover:bg-[#F5F5F7] text-gray-500 transition-colors"
              title="Camera"
            >
              <Camera className="w-5 h-5" />
            </button>
            <button
              data-testid="mic-btn"
              onClick={recording ? stopRecording : startRecording}
              className={`p-2 rounded-full transition-colors ${recording ? "bg-red-500 text-white" : "hover:bg-[#F5F5F7] text-gray-500"}`}
              title="Voice"
            >
              {recording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <button
              data-testid="send-btn"
              onClick={() => send(input)}
              disabled={thinking || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#0A2540] disabled:bg-[#E8E8ED] disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors hover:bg-[#0A2540]/90"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className={`text-[10.5px] text-center mt-2 px-2 ${hasStarted ? "text-gray-400" : "text-[#AEBBD3]"}`}>
            {t("input.disclaimer")}
          </p>
        </div>
      </div>

      <AnimatePresence>
        {showHistory && (
          <TripHistory
            onClose={() => setShowHistory(false)}
            onOpenTrip={(tItem) => {
              setTrip(tItem.trip);
              setMessages([{ role: "assistant", content: `${t("book.title")}: ${tItem.title}`, trip: tItem.trip }]);
              setShowHistory(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

